/**
 * Backend Adapter - Maps Chatbot UI patterns to our Azure/FastAPI backend
 * Replaces Supabase calls with our REST API calls
 * Maintains HIPAA compliance with secure token handling
 */

import api, { getAuthToken } from './api'

interface User {
  id: string
  email: string
  name: string
  displayName: string
  provider: 'google' | 'microsoft'
}

interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
  metadata?: Record<string, any>
}

interface PatientCase {
  id: string
  patientId: string
  patientName?: string
  messages: ChatMessage[]
  context?: Record<string, any>
  createdAt: string
  updatedAt: string
}

// Response from workspace file upload endpoint
export interface WorkspaceFileUploadResponse {
  file_id: string
  filename: string
  status: string
  content_category: 'text' | 'image'
  extracted_text_length: number
  extracted_text: string | null
}

/**
 * Upload a file to a workspace via the server-side extraction endpoint.
 * Backend extracts text and auto-saves to patient_clinical_data.
 */
export const uploadWorkspaceFile = async (
  workspaceId: string,
  file: File
): Promise<WorkspaceFileUploadResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await api.post<WorkspaceFileUploadResponse>(
    `/api/v1/workspaces/${workspaceId}/files`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  )
  return response.data
}

/**
 * Backend Adapter Class
 * Maps Supabase-style calls to our backend endpoints
 */
class BackendAdapter {
  /**
   * Get current authenticated user
   * Maps: supabase.auth.getUser() → /api/v1/me
   */
  async getUser(): Promise<User> {
    const response = await api.get<User>('/api/v1/me')
    return response.data
  }

  /**
   * Get patient case (chat) by ID
   * Maps: supabase.from('chats').select() → /api/v1/cases/{id}
   */
  async getCase(caseId: string): Promise<PatientCase> {
    const response = await api.get<PatientCase>(`/api/v1/cases/${caseId}`)
    return response.data
  }

  /**
   * Get all cases for current user
   * Maps: supabase.from('chats').select() → /api/v1/cases
   */
  async getCases(): Promise<PatientCase[]> {
    const response = await api.get<{ cases: PatientCase[] }>('/api/v1/cases')
    return response.data.cases || []
  }

  /**
   * Get canvases (artifacts) for a case
   * Maps: supabase.from('messages').select() → /api/v1/cases/{id}/canvases
   */
  async getCanvases(caseId: string): Promise<any[]> {
    const response = await api.get(`/api/v1/cases/${caseId}/canvases`)
    return response.data.canvases || []
  }

  /**
   * Get current auth header for manual fetch requests
   * Uses centralized getAuthToken from api.ts
   */
  private async getAuthHeader(): Promise<string> {
    return await getAuthToken()
  }

  /**
   * Send a message in streaming mode
   * Maps to: /api/chat (Microsoft AI Chat Protocol)
   * 
   * Returns an async generator for streaming SSE events
   */
  async *streamChat(params: {
    messages: Array<{ role: string; content: string }>
    context?: any
    attachments?: Array<{ id: string; name: string; type: string; size: number; blobUrl?: string; source: string }>
    signal?: AbortSignal
  }): AsyncGenerator<any, void, unknown> {
    // Get fresh auth token
    const authHeader = await this.getAuthHeader()

    const response = await fetch(`${api.defaults.baseURL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      },
      body: JSON.stringify({
        messages: params.messages,
        context: {
          ...(params.context || {}),
          conversation_id: params.context?.conversation_id || null
        },
        attachments: params.attachments || null
      }),
      signal: params.signal,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // Parse SSE stream (Server-Sent Events format: data: {...}\n\n)
    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    try {
      const { parseSSEStream } = await import('../utils/sseParser')
      for await (const event of parseSSEStream(reader)) {
        yield event
      }
    } catch (error) {
      // Fix #239: Don't re-wrap AbortError — let it propagate so callers can detect cancellation
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new Error(`SSE stream error: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  /**
   * Upload file to Azure Blob Storage
   * Maps: supabase.storage.upload() → /api/v1/upload
   */
  async uploadFile(file: File): Promise<{ url: string; path: string }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post('/api/v1/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    return response.data
  }

  /**
   * Execute a clinical command
   * Maps to specific NightHawk endpoints based on command type
   */
  async executeCommand(command: string, context: {
    patientId?: string
    chartSweep?: any
    edNotes?: string
    hpi?: string
  }): Promise<any> {
    switch (command) {
      case '/sweep':
      case '/chart-sweep':
        return this.chartSweep(context.edNotes || '', context)

      case '/hpi':
        return this.generateHPI(context.chartSweep, context.edNotes || '')

      case '/assessment':
      case '/ap':
        return this.streamAssessment(context.hpi || '', context)

      default:
        throw new Error(`Unknown command: ${command}`)
    }
  }

  /**
   * Generate chart sweep
   * Maps to: /api/v1/nighthawk/chart-sweep
   */
  private async chartSweep(edNotes: string, chartData?: any): Promise<any> {
    const response = await api.post('/api/v1/nighthawk/chart-sweep', {
      ed_notes: edNotes,
      chart_data: chartData
    })
    return response.data
  }

  /**
   * Generate HPI
   * Maps to: /api/v1/nighthawk/generate-hpi
   */
  private async generateHPI(chartSweep: any, edNotes: string): Promise<any> {
    const response = await api.post('/api/v1/nighthawk/generate-hpi', {
      chart_sweep: chartSweep,
      ed_notes: edNotes
    })
    return response.data
  }

  /**
   * Stream Assessment & Plan generation
   * Maps to: /api/v1/nighthawk/generate-assessment
   */
  private async *streamAssessment(
    hpi: string,
    context: any,
    signal?: AbortSignal
  ): AsyncGenerator<any, void, unknown> {
    yield* this._streamSSE(
      '/api/v1/nighthawk/generate-assessment',
      {
        hpi,
        chart_sweep: context.chartSweep,
        vitals: context.vitals,
        labs: context.labs,
        meds: context.meds,
      },
      signal,
    )
  }

  /**
   * Shared SSE streaming: auth header, fetch, error check, reader, parse.
   */
  private async *_streamSSE(url: string, body: Record<string, unknown>, signal?: AbortSignal): AsyncGenerator<CasePanelSSEEvent, void, unknown> {
    const authHeader = await this.getAuthHeader()

    const response = await fetch(`${api.defaults.baseURL}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      },
      body: JSON.stringify(body),
      signal
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new Error(`SSE error (${response.status}): ${errorText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    try {
      const { parseSSEStream } = await import('../utils/sseParser')
      for await (const event of parseSSEStream(reader)) {
        yield event as CasePanelSSEEvent
      }
    } catch (error) {
      // Fix #239: Don't re-wrap AbortError — let it propagate so callers can detect cancellation
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new Error(`SSE stream error: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      // Fix: Release the reader to prevent connection leaks on abort/error
      reader.cancel().catch(() => {})
    }
  }

  /**
   * Stream a case panel chat message via SSE
   * Routes to /api/v1/case-panel/chat for multi-agent sessions
   *
   * SSE event types: message, tool_start, tool_end, artifact, complete, error
   */
  async *streamCasePanelChat(params: {
    workspaceId: string
    agentId: string
    message: string
    mode?: string
    canvasSnapshots?: Record<string, string>
    sessionId?: string
    files?: string[]
    orchestrate?: boolean
    /** Who composed this turn. Only pipelineManager's AUTOMATED dispatches set
     * 'pipeline'; anything a physician typed leaves it unset. */
    source?: 'user' | 'pipeline'
    signal?: AbortSignal
  }): AsyncGenerator<CasePanelSSEEvent, void, unknown> {
    yield* this._streamSSE('/api/v1/case-panel/chat', {
      workspace_id: params.workspaceId,
      agent_id: params.agentId,
      message: params.message,
      mode: params.mode || 'edit',
      canvas_snapshots: params.canvasSnapshots || null,
      session_id: params.sessionId || null,
      files: params.files || null,
      // Background/pipeline turns send false so the BACKEND takes the passthrough
      // branch — client-side event dropping alone can't stop a server-side consult.
      orchestrate: params.orchestrate ?? true,
      // Separate from `orchestrate` on purpose: this says who WROTE the turn, not
      // which backend branch it takes. The one-shot manual-edit notice needs the
      // former — mode:'flow' is flattened to 'edit' server-side, and these flow
      // dispatches leave `orchestrate` at its true default, so without this an
      // automated step is indistinguishable from a physician turn and eats the
      // notice the physician's next message was supposed to get.
      source: params.source ?? 'user'
    }, params.signal)
  }
}

/**
 * SSE event shape from the case panel chat endpoint.
 * Python backend emits: data: {"event": "...", "data": {...}}\n\n
 */
export interface CasePanelSSEEvent {
  event?: string
  data: {
    content?: string
    tool_name?: string   // Backend field name (agent_runner.py)
    tool?: string        // Legacy alias — prefer tool_name
    input?: string
    output?: string
    artifact_id?: string
    artifact_type?: string
    title?: string
    conversation_id?: string
    message?: string     // Backend error field name (agent_runner.py)
    error?: string       // Legacy alias — prefer message
    code?: string        // Error code from backend (generic envelope)
    error_code?: string  // Specific error code, e.g. 'chart_data_unavailable'
    agent_id?: string
    agentId?: string
    name?: string
    [key: string]: unknown
  }
}

// Export singleton instance
export const backendAdapter = new BackendAdapter()

// Export default for easy importing
